// Pure helpers of the Email module: labels, tones, delays, tokens, summaries.
// No React and no runtime import, so `scripts/tests/email-module.test.mjs`
// runs them under node:test directly.

import type {
  AddPreviewSummary,
  CampaignStatus,
  CompanyEmailStatus,
  ContactEmailStatus,
  ContentState,
  DelayUnit,
  Eligibility,
  EmailProjectAccess,
  EmailScope,
  LaunchResult,
  MessageStatus,
  OutreachKind,
  PerformanceDay,
  ProviderMode,
  StepImpact,
  StepTimeline,
  SyncResult,
} from "@/api/email";

export interface Tone {
  label: string;
  /** Pill classes: background, text, border. */
  pill: string;
  dot: string;
  hint?: string;
}

export const CONTACT_STATUS: Record<ContactEmailStatus, Tone> = {
  EMAIL_READY: {
    label: "Ready",
    pill: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    hint: "In the campaign, not handed to Lemlist yet. Nothing has been sent.",
  },
  EMAIL_QUEUED: {
    label: "Launched",
    pill: "bg-sky-50 text-sky-700 border-sky-200",
    dot: "bg-sky-500",
    hint: "Launched: handed to Lemlist with every email rendered. Its first email is not sent yet — Lemlist sends it on the campaign's days and hours, one email every few minutes per mailbox.",
  },
  EMAIL_ACTIVE: {
    label: "In sequence",
    pill: "bg-indigo-50 text-indigo-700 border-indigo-200",
    dot: "bg-indigo-500",
    hint: "At least one email sent; the next steps follow their delays.",
  },
  EMAIL_PAUSED: {
    label: "Paused",
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    hint: "Nothing more is sent until the contact is resumed.",
  },
  EMAIL_REPLIED: {
    label: "Replied",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    hint: "The contact replied; the sequence stopped.",
  },
  EMAIL_BOUNCED: {
    label: "Bounced",
    pill: "bg-rose-50 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
    hint: "The address bounced; never added again without an explicit choice.",
  },
  EMAIL_COMPLETED: {
    label: "Completed",
    pill: "bg-teal-50 text-teal-700 border-teal-200",
    dot: "bg-teal-500",
    hint: "Every step was sent, without a reply.",
  },
  EMAIL_REMOVED: {
    label: "Removed",
    pill: "bg-white text-slate-500 border-slate-200",
    dot: "bg-slate-300",
    hint: "Taken out of the campaign; kept for the history.",
  },
  EMAIL_UNSUBSCRIBED: {
    label: "Unsubscribed",
    pill: "bg-zinc-100 text-zinc-600 border-zinc-200",
    dot: "bg-zinc-500",
    hint: "Asked to receive nothing more; never added again without an explicit choice.",
  },
};

/** Order of the status filter chips, most actionable first. */
export const CONTACT_STATUS_ORDER: ContactEmailStatus[] = [
  "EMAIL_READY",
  "EMAIL_QUEUED",
  "EMAIL_ACTIVE",
  "EMAIL_PAUSED",
  "EMAIL_REPLIED",
  "EMAIL_COMPLETED",
  "EMAIL_BOUNCED",
  "EMAIL_UNSUBSCRIBED",
  "EMAIL_REMOVED",
];

export const COMPANY_STATUS: Record<CompanyEmailStatus, Tone> = {
  EMAIL_READY: { ...CONTACT_STATUS.EMAIL_READY, hint: "Contacts wait to start; nothing sent." },
  EMAIL_IN_PROGRESS: {
    label: "In progress",
    pill: "bg-indigo-50 text-indigo-700 border-indigo-200",
    dot: "bg-indigo-500",
    hint: "At least one contact is launched or in sequence.",
  },
  EMAIL_PAUSED: { ...CONTACT_STATUS.EMAIL_PAUSED, hint: "Every running contact is paused." },
  EMAIL_REPLIED: { ...CONTACT_STATUS.EMAIL_REPLIED, hint: "At least one contact replied." },
  EMAIL_COMPLETED: CONTACT_STATUS.EMAIL_COMPLETED,
  EMAIL_BOUNCED: CONTACT_STATUS.EMAIL_BOUNCED,
  EMAIL_UNSUBSCRIBED: CONTACT_STATUS.EMAIL_UNSUBSCRIBED,
};

export const CAMPAIGN_STATUS: Record<CampaignStatus, Tone> = {
  DRAFT: {
    label: "Draft",
    pill: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
    hint: "Being built. Contacts are handed to Lemlist once it is active.",
  },
  ACTIVE: {
    label: "Active",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    hint: "Lemlist sends to the launched leads on the campaign's days and hours. New leads go out only when you launch them (Launch tab).",
  },
  PAUSED: {
    label: "Paused",
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    hint: "Lemlist sends nothing; no new contact is handed over.",
  },
  ARCHIVED: {
    label: "Archived",
    pill: "bg-zinc-100 text-zinc-500 border-zinc-200",
    dot: "bg-zinc-400",
    hint: "Kept for the history; nothing is sent.",
  },
};

/** What is on record about an address (D2): a stored proof, or nothing. */
export const VERIFICATION_HINT = {
  VERIFIED:
    "Verified by Apollo when the CFO workflow admitted this exact address. A verified address can still bounce.",
  NONE: "Prism holds no check of this address. That alone blocks nothing.",
} as const;

export function verificationLabel(verification: { state: "VERIFIED" | "NONE" } | null | undefined): string {
  return verification?.state === "VERIFIED" ? "Verified" : "No verification on record";
}

/** Said wherever a company of the legacy workflow cannot join a campaign. */
export const LEGACY_ENROLLED = "Already enrolled via legacy workflow";

export const SCOPE: Record<EmailScope, Tone> = {
  CFO: { label: "CFO", pill: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  MA: { label: "M&A", pill: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500" },
};

export const PROVIDER_MODE: Record<ProviderMode, Tone> = {
  NONE: {
    label: "Not synced",
    pill: "bg-white text-slate-500 border-slate-200",
    dot: "bg-slate-300",
    hint: "Lemlist does not know this campaign yet.",
  },
  DRY_RUN: {
    label: "Dry run",
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    hint: "Synced in simulation: nothing reached Lemlist.",
  },
  LIVE: {
    label: "Lemlist",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    hint: "Lives in Lemlist.",
  },
};

export const ELIGIBILITY: Record<Eligibility, Tone> = {
  READY: {
    label: "Ready",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
  NO_EMAIL: {
    label: "No email",
    pill: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
    hint: "Without an email address, nothing can be sent.",
  },
  ALREADY_IN_CAMPAIGN: {
    label: "Already in this campaign",
    pill: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  },
  IN_OTHER_CAMPAIGN: {
    label: "In another campaign",
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    hint: "Moved only if you ask for it; its progress there is not carried over.",
  },
  LEGACY_ENROLLED: {
    label: LEGACY_ENROLLED,
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    hint:
      "The company is in a Lemlist sequence of the legacy workflow (its Email sequence panel). " +
      "Remove it there to email its contacts from here — never both ways.",
  },
  UNSUBSCRIBED: {
    label: "Unsubscribed",
    pill: "bg-zinc-100 text-zinc-600 border-zinc-200",
    dot: "bg-zinc-500",
    hint: "Added only if you explicitly include unsubscribed contacts.",
  },
  BOUNCED: {
    label: "Bounced",
    pill: "bg-rose-50 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
    hint: "The address bounced before.",
  },
  INVALID_EMAIL: {
    label: "Invalid email",
    pill: "bg-rose-50 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
    hint: "The address is not shaped like one: nothing can be sent to it.",
  },
};

export const ACTION_LABEL: Record<"ADD" | "MOVE" | "SKIP", string> = {
  ADD: "Add",
  MOVE: "Move here",
  SKIP: "Skip",
};

/** Render errors, as the editor names them. */
export const RENDER_ERROR_LABEL: Record<string, string> = {
  missing_variable: "Missing value",
  unknown_variable: "Unknown variable",
  malformed_token: "Not a variable",
  ai_not_generated: "AI not generated",
  no_contact: "No contact",
  no_company: "No linked company",
  no_email: "No email",
  empty_subject: "Empty subject",
  empty_body: "Empty body",
};

export const EVENT_LABEL: Record<string, string> = {
  SENT: "Sent",
  OPENED: "Opened",
  CLICKED: "Clicked",
  REPLIED: "Replied",
  BOUNCED: "Bounced",
  FAILED: "Failed",
  UNSUBSCRIBED: "Unsubscribed",
  PAUSED: "Paused",
  RESUMED: "Resumed",
  COMPLETED: "Completed",
};

export const WEEKDAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
  { value: 7, short: "Sun", long: "Sunday" },
];

// ── delays ───────────────────────────────────────────────────────────────

export function delayDays(value: number, unit: DelayUnit): number {
  return unit === "WEEKS" ? value * 7 : value;
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "3 days", "2 weeks", "Same day". */
export function delayLabel(value: number, unit: DelayUnit): string {
  if (value === 0) return "Same day";
  return unit === "WEEKS" ? plural(value, "week") : plural(value, "day");
}

/** The day each step goes out, counted from the first email (day 0). */
export function stepDays(steps: { delay_value: number; delay_unit: DelayUnit }[]): number[] {
  let day = 0;
  return steps.map((step) => (day += delayDays(step.delay_value, step.delay_unit)));
}

/** "Mon–Fri", "Every day", "Mon, Wed, Fri": a campaign's sending days (ISO 1–7). */
export function sendingDaysLabel(days: number[]): string {
  const sorted = [...new Set(days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
  if (!sorted.length) return "No sending day";
  if (sorted.length === 7) return "Every day";
  const short = (d: number) => WEEKDAYS[d - 1].short;
  const consecutive = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (consecutive && sorted.length > 2) return `${short(sorted[0])}–${short(sorted[sorted.length - 1])}`;
  return sorted.map(short).join(", ");
}

// ── tokens ───────────────────────────────────────────────────────────────

const BRACES = /\{\{(.*?)\}\}/gs;
/** The renderer's grammar: a Unicode document alias or `namespace.key`, optionally `| "fallback"` (straight or curly quotes). */
const INNER = /^\s*([\p{L}\p{N}_]+(?:\.[a-z0-9_]+)*)\s*(?:\|\s*(?:"([^"]*)"|“([^”]*)”)\s*)?$/u;

/** What is between the braces → its token and fallback text; null when it is not a
 *  token. An AI variable takes no fallback (the renderer refuses it). */
export function parseToken(inner: string): { token: string; fallback: string | null } | null {
  const match = INNER.exec(inner);
  if (!match) return null;
  const fallback = match[2] ?? match[3] ?? null;
  if (fallback !== null && match[1].startsWith("ai.")) return null;
  return { token: match[1], fallback };
}

/** Well-formed tokens, in order of first appearance. */
export function tokensIn(...templates: string[]): string[] {
  const seen: string[] = [];
  for (const template of templates) {
    for (const match of (template ?? "").matchAll(BRACES)) {
      const parsed = parseToken(match[1]);
      if (parsed && !seen.includes(parsed.token)) seen.push(parsed.token);
    }
  }
  return seen;
}

/** Tokens used at least once without a fallback: a contact without their
 *  value is not sent. */
export function tokensWithoutFallback(...templates: string[]): string[] {
  const seen: string[] = [];
  for (const template of templates) {
    for (const match of (template ?? "").matchAll(BRACES)) {
      const parsed = parseToken(match[1]);
      if (parsed && parsed.fallback === null && !seen.includes(parsed.token)) seen.push(parsed.token);
    }
  }
  return seen;
}

export function aiKeysIn(...templates: string[]): string[] {
  return tokensIn(...templates)
    .filter((token) => token.startsWith("ai."))
    .map((token) => token.slice(3));
}

export const AI_KEY = /^[a-z][a-z0-9_]{0,63}$/;

/** "Company context" → "company_context": a valid AI key from a label. */
export function slugifyKey(label: string): string {
  const key = label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9_]+/, "")
    .slice(0, 64);
  return key;
}

/** Insert `{{token}}` at the caret (replacing a selection); returns the new
 *  text and where the caret goes. */
export function insertToken(
  text: string,
  start: number,
  end: number,
  token: string,
  /** With a fallback: `{{token | ""}}`, the caret between the quotes to type it. */
  withFallback = false,
): { text: string; caret: number } {
  const from = Math.max(0, Math.min(start, text.length));
  const to = Math.max(from, Math.min(end, text.length));
  const snippet = withFallback ? `{{${token} | ""}}` : `{{${token}}}`;
  const caret = from + snippet.length - (withFallback ? 3 : 0);
  return { text: text.slice(0, from) + snippet + text.slice(to), caret };
}

export type Segment = { kind: "text" | "token" | "bad"; value: string };

/** Split a template so the editor can highlight its tokens. */
export function segments(template: string): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  for (const match of template.matchAll(BRACES)) {
    const index = match.index ?? 0;
    if (index > last) out.push({ kind: "text", value: template.slice(last, index) });
    out.push({ kind: parseToken(match[1]) ? "token" : "bad", value: match[0] });
    last = index + match[0].length;
  }
  if (last < template.length) out.push({ kind: "text", value: template.slice(last) });
  return out;
}

// ── summaries ────────────────────────────────────────────────────────────

/** The wizard's preview, in one line: "68 ready · 2 without email · …". */
export function summaryParts(summary: AddPreviewSummary): string[] {
  const parts: [number, string][] = [
    [summary.ready, "ready"],
    [summary.no_email, "without email"],
    [summary.already_in_campaign, "already in this campaign"],
    [summary.in_other_campaign, "in another campaign"],
    [summary.legacy_enrolled, "already enrolled via legacy workflow"],
    [summary.unsubscribed, "unsubscribed"],
    [summary.bounced, "bounced"],
    [summary.invalid_email ?? 0, "with an invalid email"],
  ];
  return parts.filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`);
}

/** Leads a campaign holds now: every membership but the removed ones. */
export function leadsInCampaign(counts: Record<string, number>): number {
  return Object.entries(counts)
    .filter(([status]) => status !== "EMAIL_REMOVED")
    .reduce((sum, [, n]) => sum + n, 0);
}

/** "12 · 8.3%": leads who replied, and their share of the leads reached —
 *  never a count of reply messages. Without lead stats, the replied leads by
 *  status and no rate (it is not demonstrable). */
export function campaignReplies(campaign: {
  contacts_by_status: Record<string, number>;
  lead_stats?: { reached: number; replied: number } | null;
}): { count: number | null; rate: string | null; hint: string } {
  const stats = campaign.lead_stats;
  if (stats) {
    return {
      count: stats.replied,
      rate: stats.reached ? percent(stats.replied, stats.reached) : null,
      hint: stats.reached
        ? `${stats.replied} of ${stats.reached} leads reached replied at least once.`
        : "No lead reached yet: no reply rate.",
    };
  }
  const replied = campaign.contacts_by_status.EMAIL_REPLIED ?? 0;
  return { count: replied, rate: null, hint: "Leads whose sequence stopped on a reply." };
}

/** "8.3%" — one decimal under 10%, none above. */
export function percent(part: number, whole: number): string {
  if (!whole) return "—";
  const value = (part / whole) * 100;
  return `${value < 10 && value > 0 ? value.toFixed(1) : Math.round(value)}%`;
}

// ── performance ──────────────────────────────────────────────────────────

/** A rate (0–1) as the page shows it: "8.3%", "42%" — "—" when there is no
 *  rate (nobody reached), never 0%. */
export function rateLabel(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";
  const value = rate * 100;
  return `${value < 10 && value > 0 ? value.toFixed(1) : Math.round(value)}%`;
}

export type PeriodPreset = "all" | "7d" | "30d" | "custom";

/** The launch cohort of a preset, as ISO days (inclusive), from `today`. */
export function periodRange(
  preset: PeriodPreset,
  today: Date,
  custom: { from?: string | null; to?: string | null } = {},
): { launched_from: string | null; launched_to: string | null } {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (preset === "all") return { launched_from: null, launched_to: null };
  if (preset === "custom") return { launched_from: custom.from || null, launched_to: custom.to || null };
  const days = preset === "7d" ? 6 : 29;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);
  return { launched_from: iso(start), launched_to: iso(today) };
}

/** What each figure counts — Prism's definitions, shown beside it. */
export const METRIC_HINT = {
  in_campaign: "Leads in the campaign now (removed ones excluded), whatever the period.",
  to_launch: "Leads never handed to Lemlist and still live (ready or paused), now.",
  launched: "Leads handed to Lemlist for real. A dry run's are counted apart.",
  reached: "Leads sent at least one email. Every rate below is over them.",
  opened: "Leads who opened at least one email (pixel tracking: blocked images hide opens).",
  clicked: "Leads who clicked at least one link.",
  replied: "Leads who replied at least once.",
  bounced: "Leads with a bounced email, or ended as bounced.",
  unsubscribed: "Leads who unsubscribed (Lemlist's event, or ended so).",
  sent: "Emails sent — one per lead and step.",
  opened_messages: "Emails opened at least once.",
  opens: "Every open, repeats included.",
  clicks: "Every click, repeats included.",
  replies: "Replies received (a lead may reply more than once).",
  bounced_messages: "Emails that bounced.",
  entered: "Leads this step's email was sent to. The step's rates are over them.",
} as const;

export type ActivityKey = "launched" | "sent" | "opened" | "clicked" | "replied" | "bounced" | "unsubscribed";

export const ACTIVITY_KEYS: ActivityKey[] = ["launched", "sent", "opened", "clicked", "replied", "bounced", "unsubscribed"];

/** One bar group of the activity chart: a day, or a week (from its Monday). */
export type ActivityPoint = { day: string } & Record<ActivityKey, number>;

function isoShift(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function isoDiff(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

function isoMonday(iso: string): string {
  const weekday = (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
  return isoShift(iso, -weekday);
}

/** Today in a timezone, as an ISO day (YYYY-MM-DD). */
export function isoDayIn(timezone: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/**
 * The activity chart's series: every day from the first one (the period's
 * start, else the first activity) to `today` — days without activity as
 * zeros, at least a week shown — or week by week, from Mondays, past 62 days.
 */
export function activitySeries(
  days: PerformanceDay[],
  { today, from = null }: { today: string; from?: string | null },
): { points: ActivityPoint[]; weekly: boolean } {
  const byDay = new Map(days.map((d) => [d.day, d]));
  const known = days.map((d) => d.day).sort();
  const last = [today, ...known].sort()[known.length];
  let first = [...(from ? [from] : []), ...known].sort()[0] ?? last;
  if (first > last) first = last;
  if (isoDiff(first, last) < 6) first = isoShift(last, -6);
  const weekly = isoDiff(first, last) > 62;
  const points = new Map<string, ActivityPoint>();
  for (let iso = first; iso <= last; iso = isoShift(iso, 1)) {
    const key = weekly ? isoMonday(iso) : iso;
    let point = points.get(key);
    if (!point) {
      point = { day: key, launched: 0, sent: 0, opened: 0, clicked: 0, replied: 0, bounced: 0, unsubscribed: 0 };
      points.set(key, point);
    }
    const row = byDay.get(iso);
    if (row) for (const k of ACTIVITY_KEYS) point[k] += row[k];
  }
  return { points: [...points.values()], weekly };
}

// ── outreach ─────────────────────────────────────────────────────────────

export const OUTREACH_KIND: Record<OutreachKind, Tone> = {
  SCHEDULED: {
    label: "Scheduled",
    pill: "bg-sky-50 text-sky-700 border-sky-200",
    dot: "bg-sky-500",
    hint: "Estimated day: Lemlist sends it within the campaign's sending window, at a time of its choosing.",
  },
  OVERDUE: {
    label: "Overdue",
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    hint: "Estimated for a day already past and not reported sent yet: Lemlist's queue, a pause there, or events not synced yet.",
  },
  SENT: { label: "Sent", pill: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  REPLIED: { label: "Reply", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  BOUNCED: { label: "Bounce", pill: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  UNSUBSCRIBED: { label: "Unsubscribe", pill: "bg-zinc-100 text-zinc-600 border-zinc-200", dot: "bg-zinc-500" },
};

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseDay(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** The days a Day or Week view shows around `day` (a week runs Monday–Sunday). */
export function outreachWindow(view: "day" | "week", day: string): { start: string; end: string } {
  const date = parseDay(day);
  if (view === "day") return { start: isoDay(date), end: isoDay(date) };
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate() - ((date.getDay() + 6) % 7));
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  return { start: isoDay(monday), end: isoDay(sunday) };
}

/** The previous or next Day / Week. */
export function shiftDay(view: "day" | "week", day: string, delta: number): string {
  const date = parseDay(day);
  return isoDay(new Date(date.getFullYear(), date.getMonth(), date.getDate() + delta * (view === "day" ? 1 : 7)));
}

export function todayIso(now: Date = new Date()): string {
  return isoDay(now);
}

/** "Today · Tue 6 Oct", "Tomorrow · Wed 7 Oct", "Mon 12 Oct". */
export function dayLabel(day: string, today: string): string {
  const date = parseDay(day);
  const text = date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  if (day === today) return `Today · ${text}`;
  if (day === shiftDay("day", today, 1)) return `Tomorrow · ${text}`;
  if (day === shiftDay("day", today, -1)) return `Yesterday · ${text}`;
  return text;
}

/** Items in day order, grouped by their day. */
export function groupByDay<T extends { day: string }>(items: T[]): { day: string; items: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const list = groups.get(item.day) ?? [];
    list.push(item);
    groups.set(item.day, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, list]) => ({ day, items: list }));
}

/** What one sync call did, in plain words. */
export function syncSummary(result: SyncResult): string {
  const parts: string[] = [];
  if (result.campaign_created) parts.push("campaign created");
  if (result.steps_created) parts.push(plural(result.steps_created, "step") + " created");
  if (result.contacts_pushed) parts.push(plural(result.contacts_pushed, "contact") + " handed over");
  if (result.contacts_updated)
    parts.push(plural(result.contacts_updated, "contact") + " updated");
  if (result.contacts_held) parts.push(plural(result.contacts_held, "contact") + " on hold in Lemlist");
  if (result.events_ingested) parts.push(plural(result.events_ingested, "event") + " received");
  if (!parts.length) parts.push("already up to date");
  const head = result.external ? "Synced with Lemlist" : "Dry run";
  return `${head}: ${parts.join(", ")}.`;
}

/** Who a new step reaches, in the words the add dialog shows. Never says or
 *  implies it reaches anyone retroactively. */
export function impactLines(impact: StepImpact): string[] {
  const lines = [`${plural(impact.will_receive, "contact")} will receive this new step.`];
  if (impact.already_past)
    lines.push(
      `${plural(impact.already_past, "contact")} ${impact.already_past === 1 ? "has" : "have"} already passed this position.`,
    );
  if (impact.completed) lines.push(`${plural(impact.completed, "contact")} already completed the sequence.`);
  const others = [
    impact.paused ? `${impact.paused} paused (they get it if resumed before reaching it)` : "",
    impact.replied ? `${impact.replied} replied` : "",
    impact.ended ? `${impact.ended} bounced or unsubscribed` : "",
    impact.removed ? `${impact.removed} removed` : "",
  ].filter(Boolean);
  if (others.length) lines.push(`Not now: ${others.join(", ")}.`);
  return lines;
}

// ── launch ───────────────────────────────────────────────────────────────

/** Several launch calls, added up as one. */
export function mergeLaunch(total: LaunchResult | null, next: LaunchResult): LaunchResult {
  if (!total) return next;
  return {
    ...next,
    campaign_created: total.campaign_created || next.campaign_created,
    steps_created: total.steps_created + next.steps_created,
    events_ingested: total.events_ingested + next.events_ingested,
    contacts_updated: total.contacts_updated + next.contacts_updated,
    contacts_pushed: total.contacts_pushed + next.contacts_pushed,
    contacts_failed: total.contacts_failed + next.contacts_failed,
    contacts_held: total.contacts_held + next.contacts_held,
    launched_ids: [...total.launched_ids, ...next.launched_ids],
    failures: [...total.failures, ...next.failures],
    skipped: [...total.skipped, ...next.skipped],
    stopped: total.stopped || next.stopped,
    errors: [...total.errors, ...next.errors],
    notes: next.notes,
  };
}

/** Whether "Launch all eligible" should call again: leads went out, some
 *  remain, today's limit has room, and Lemlist did not refuse launches. */
export function launchHasMore(result: LaunchResult): boolean {
  return (
    !result.stopped &&
    result.contacts_pushed > 0 &&
    result.remaining > 0 &&
    result.pushed_today < result.daily_limit
  );
}

/** What a launch did, in one line. */
export function launchSummary(result: LaunchResult): string {
  const head = result.external ? "Launched to Lemlist" : "Dry run";
  const parts = [plural(result.contacts_pushed, "lead") + " launched"];
  if (result.failures.length) parts.push(`${result.failures.length} refused`);
  if (result.skipped.length) parts.push(`${result.skipped.length} not to launch`);
  return `${head}: ${parts.join(", ")}.`;
}

/** [1..7] in slices of 3 → [[1,2,3],[4,5,6],[7]]. */
export function chunks<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += Math.max(1, size)) out.push(items.slice(i, i + Math.max(1, size)));
  return out;
}

/** Whether another sync call would make progress. */
export function syncHasMore(result: SyncResult): boolean {
  return (
    (result.remaining > 0 && result.contacts_pushed > 0 && result.pushed_today < result.daily_limit) ||
    (result.updates_remaining > 0 && result.contacts_updated > 0)
  );
}

// ── time ─────────────────────────────────────────────────────────────────

/** "just now", "5m ago", "3h ago", "2d ago", then the date: short enough for a table. */
export function shortAgo(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "—";
  const date = new Date(value);
  const seconds = Math.max(0, Math.round((now.getTime() - date.getTime()) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// ── messages ─────────────────────────────────────────────────────────────

/** One email's state in a contact's workflow. `null`: not handed to Lemlist. */
export const MESSAGE_STATUS: Record<MessageStatus | "NOT_HANDED", Tone> = {
  NOT_HANDED: {
    label: "Not handed over",
    pill: "bg-white text-slate-500 border-slate-200",
    dot: "bg-slate-300",
    hint: "Rendered from the template when Prism hands the contact to Lemlist.",
  },
  QUEUED: {
    label: "Scheduled",
    pill: "bg-sky-50 text-sky-700 border-sky-200",
    dot: "bg-sky-500",
    hint: "Lemlist holds this exact email and sends it on its day.",
  },
  SENT: {
    label: "Sent",
    pill: "bg-indigo-50 text-indigo-700 border-indigo-200",
    dot: "bg-indigo-500",
    hint: "Frozen: this is exactly what was sent.",
  },
  DELIVERED: {
    label: "Delivered",
    pill: "bg-indigo-50 text-indigo-700 border-indigo-200",
    dot: "bg-indigo-500",
  },
  OPENED: {
    label: "Opened",
    pill: "bg-violet-50 text-violet-700 border-violet-200",
    dot: "bg-violet-500",
  },
  CLICKED: {
    label: "Clicked",
    pill: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    dot: "bg-fuchsia-500",
  },
  REPLIED: {
    label: "Replied",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
  BOUNCED: {
    label: "Bounced",
    pill: "bg-rose-50 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
  },
};

export function messageTone(status: MessageStatus | null): Tone {
  return MESSAGE_STATUS[status ?? "NOT_HANDED"];
}

/** A stored provider render is not a promise to send after the sequence stops. */
export function workflowHold(
  step: Pick<StepTimeline, "status" | "sent_at">,
  contactStatus: ContactEmailStatus,
  dryRun: boolean,
): Tone | null {
  if (step.sent_at || (step.status !== null && step.status !== "QUEUED")) return null;
  const terminal = [
    "EMAIL_REPLIED", "EMAIL_BOUNCED", "EMAIL_UNSUBSCRIBED", "EMAIL_COMPLETED", "EMAIL_REMOVED",
  ].includes(contactStatus);
  if (terminal) {
    return {
      label: "Stopped",
      pill: "bg-slate-100 text-slate-600 border-slate-200",
      dot: "bg-slate-400",
      hint: `Not scheduled: the sequence ended (${CONTACT_STATUS[contactStatus].label.toLowerCase()}).`,
    };
  }
  if (contactStatus === "EMAIL_PAUSED") {
    return { ...CONTACT_STATUS.EMAIL_PAUSED, hint: "Not scheduled while this contact is paused." };
  }
  if (dryRun) {
    return {
      label: "Dry run",
      pill: "bg-amber-50 text-amber-700 border-amber-200",
      dot: "bg-amber-400",
      hint: "Dry run: this email is simulated, not scheduled in Lemlist.",
    };
  }
  return null;
}

/** What can be said of a reply's content. */
export const REPLY_CONTENT: Record<Exclude<ContentState, "RENDERED">, string> = {
  AVAILABLE: "",
  PENDING: "Reply received — its text is read from Lemlist at the next sync.",
  UNAVAILABLE: "Reply detected — content unavailable from provider",
};

/** "Opened 3×", "Opened · Clicked 2×": what a sent email's tracking says. */
export function trackingParts(step: Pick<StepTimeline, "open_count" | "click_count">): string[] {
  const parts: string[] = [];
  if (step.open_count) parts.push(step.open_count > 1 ? `Opened ${step.open_count}×` : "Opened");
  if (step.click_count) parts.push(step.click_count > 1 ? `Clicked ${step.click_count}×` : "Clicked");
  return parts;
}

// ── Email Sync (Engine Control) ──────────────────────────────────────────

/** Whether the user may activate, pause, archive or restore this campaign
 *  here. A LIVE campaign's state reaches the provider: that also needs
 *  outreach.sync. The backend checks the same, and stays the authority. */
export function canChangeCampaignState(
  access: Pick<EmailProjectAccess, "can_manage" | "can_sync"> | null | undefined,
  campaign: { provider_mode: ProviderMode; provider_legacy?: boolean; execution_lock?: unknown },
): boolean {
  // A previous Lemlist team's campaign is history, a locked pilot is for
  // review: nothing changes either.
  if (!access?.can_manage || campaign.provider_legacy || campaign.execution_lock) return false;
  return campaign.provider_mode !== "LIVE" || access.can_sync;
}

/** Deleting is for the campaign's managers. One live in Lemlist (and not
 *  archived) is paused there first, which takes the right to sync. */
export function canDeleteCampaign(
  access: Pick<EmailProjectAccess, "can_manage" | "can_sync"> | null | undefined,
  campaign: { provider_mode: ProviderMode; provider_legacy?: boolean; status: string; execution_lock?: unknown },
): boolean {
  if (!access?.can_manage) return false;
  if (campaign.execution_lock && campaign.provider_mode === "LIVE") return false;
  if (campaign.provider_legacy || campaign.status === "ARCHIVED" || campaign.provider_mode !== "LIVE") return true;
  return access.can_sync;
}

/** The delete confirmation: what goes, what stays, where. */
export function deleteCampaignText(campaign: {
  provider_mode: ProviderMode;
  provider_legacy?: boolean;
}): string {
  const lemlist =
    campaign.provider_mode === "LIVE" && !campaign.provider_legacy
      ? " Lemlist keeps the campaign, paused: its API cannot delete one, so delete it there too if you want it gone."
      : "";
  return (
    "Its steps, contacts, rendered emails and conversations are deleted from Prism; the companies' timelines keep what was sent." +
    lemlist +
    " This cannot be undone."
  );
}

export const PREVIOUS_TEAM_NOTE =
  "This campaign belongs to the previous Lemlist team: Prism keeps its history, read-only. Duplicate it to run it again in the current team.";

export const EMAIL_SYNC_JOBS = ["email_sync_cfo", "email_sync_ma"] as const;

export function isEmailSyncJob(jobId: string): boolean {
  return (EMAIL_SYNC_JOBS as readonly string[]).includes(jobId);
}

/** The counters of an Email Sync run, in Engine Control's order. */
export function emailSyncCounters(stats: Record<string, unknown> | null | undefined): {
  key: string;
  label: string;
  value: number;
}[] {
  const read = (key: string) => (typeof stats?.[key] === "number" ? (stats[key] as number) : 0);
  return [
    { key: "campaigns", label: "Campaigns", value: read("campaigns") },
    { key: "contacts", label: "Contacts", value: read("contacts") },
    { key: "new_events", label: "New events", value: read("new_events") },
    { key: "sent", label: "Sent", value: read("sent") },
    { key: "opened", label: "Opened", value: read("opened") },
    { key: "replies", label: "Replies", value: read("replies") },
    { key: "bounces", label: "Bounces", value: read("bounces") },
    { key: "errors", label: "Errors", value: read("errors") },
  ];
}
